import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

function isElectronRuntime(): boolean {
  return typeof window !== "undefined" && Boolean(window.Electron?.ipcRenderer);
}

function debugLayoutMedia(
  message: string,
  meta?: Record<string, unknown>,
): void {
  if (!isElectronRuntime()) return;
  if (meta) {
    console.info(`[LayoutMediaDebug] ${message}`, meta);
    return;
  }
  console.info(`[LayoutMediaDebug] ${message}`);
}

type ProtectedMediaCacheEntry =
  | { status: "loading"; promise: Promise<string> }
  | { status: "loaded"; objectUrl: string }
  | { status: "error" };

interface ProtectedMediaCacheValue {
  version: number;
  getObjectUrl: (src: string) => string | undefined;
  preload: (src: string) => Promise<string>;
}

export const ProtectedMediaCacheContext =
  createContext<ProtectedMediaCacheValue | null>(null);

function isPrivateLayoutMediaSrc(src?: string): boolean {
  return Boolean(
    src?.includes("/user-layouts/images/") ||
      src?.includes("/user-layouts/videos/"),
  );
}

function isRuntimeLayoutMediaSrc(src?: string): boolean {
  return Boolean(
    src?.includes("/runtime-layouts/assets/images/") ||
      src?.includes("/runtime-layouts/assets/videos/"),
  );
}

/**
 * Returns `true` for layout image/video URLs that should be preloaded into a
 * renderer-local blob cache.
 *
 * This includes both private `/user-layouts/*` endpoints (JWT required) and
 * public runtime relay endpoints `/runtime-layouts/assets/*`.
 */
export function isProtectedMediaSrc(src?: string): boolean {
  return isPrivateLayoutMediaSrc(src) || isRuntimeLayoutMediaSrc(src);
}

function getTokenFromCookie(): string | null {
  if (typeof document === "undefined") return null;
  const tokenCookie = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith("token="));
  if (!tokenCookie) return null;
  const [, rawValue = ""] = tokenCookie.split("=");
  return decodeURIComponent(rawValue);
}

async function getAuthToken(): Promise<string | null> {
  const cookieToken = getTokenFromCookie();
  if (cookieToken) {
    debugLayoutMedia("Using cookie token for protected media fetch");
    return cookieToken;
  }
  if (typeof window !== "undefined" && window.Electron?.ipcRenderer) {
    try {
      const response = await window.Electron.ipcRenderer.invoke(
        "account:get-access-token",
      );
      if (response?.success && response?.data) {
        debugLayoutMedia("Using IPC token for protected media fetch", {
          hasToken: true,
        });
        return String(response.data);
      }
      debugLayoutMedia("IPC token lookup returned empty response", {
        success: Boolean(response?.success),
        hasToken: Boolean(response?.data),
      });
    } catch {
      debugLayoutMedia("IPC token lookup failed");
    }
  }
  debugLayoutMedia("No auth token available for protected media fetch");
  return null;
}

export async function fetchProtectedMediaObjectUrl(
  src: string,
): Promise<string> {
  const headers: Record<string, string> = {};
  const privateMedia = isPrivateLayoutMediaSrc(src);
  debugLayoutMedia("Fetching protected media", {
    src,
    privateMedia,
    runtimeMedia: isRuntimeLayoutMediaSrc(src),
  });
  if (isPrivateLayoutMediaSrc(src)) {
    const token = await getAuthToken();
    if (!token) {
      debugLayoutMedia("Missing auth token for private media", { src });
      throw new Error("MISSING_AUTH_TOKEN");
    }
    headers.Authorization = `Bearer ${token}`;
  }
  const response = await fetch(src, { headers });
  debugLayoutMedia("Protected media response received", {
    src,
    status: response.status,
    ok: response.ok,
  });
  if (!response.ok) {
    throw new Error(`HTTP_${response.status}`);
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

export function useProtectedMediaCache(
  preloadSources: readonly string[],
): ProtectedMediaCacheValue {
  const cacheRef = useRef<Map<string, ProtectedMediaCacheEntry>>(new Map());
  const [version, setVersion] = useState(0);

  const bumpVersion = useCallback(() => {
    setVersion((current) => current + 1);
  }, []);

  const getObjectUrl = useCallback((src: string) => {
    const entry = cacheRef.current.get(src);
    return entry?.status === "loaded" ? entry.objectUrl : undefined;
  }, []);

  const preload = useCallback(
    (src: string): Promise<string> => {
      if (!isProtectedMediaSrc(src)) {
        return Promise.resolve(src);
      }

      const existing = cacheRef.current.get(src);
      if (existing?.status === "loaded") {
        return Promise.resolve(existing.objectUrl);
      }
      if (existing?.status === "loading") {
        return existing.promise;
      }
      if (existing?.status === "error") {
        return Promise.reject(new Error("PROTECTED_MEDIA_PRELOAD_FAILED"));
      }

      const promise = fetchProtectedMediaObjectUrl(src)
        .then((objectUrl) => {
          cacheRef.current.set(src, { status: "loaded", objectUrl });
          bumpVersion();
          return objectUrl;
        })
        .catch((error) => {
          cacheRef.current.set(src, { status: "error" });
          bumpVersion();
          throw error;
        });

      cacheRef.current.set(src, { status: "loading", promise });
      return promise;
    },
    [bumpVersion],
  );

  useEffect(() => {
    preloadSources.forEach((src) => {
      if (!isProtectedMediaSrc(src)) return;
      void preload(src).catch(() => {
        // Individual media failures should not block layout rendering.
      });
    });
  }, [preload, preloadSources]);

  useEffect(
    () => () => {
      for (const entry of cacheRef.current.values()) {
        if (entry.status === "loaded") {
          URL.revokeObjectURL(entry.objectUrl);
        }
      }
      cacheRef.current.clear();
    },
    [],
  );

  return useMemo(
    () => ({
      version,
      getObjectUrl,
      preload,
    }),
    [getObjectUrl, preload, version],
  );
}
